import jwtDecode from "jwt-decode";
import api from "../api";
import {
  REFRESH_TOKEN,
  JWT_RETRY_TIMEOUT,
  JWT_REFRESH_WINDOW_SEC,
} from "../constants";
import { setAccessToken, setTokenExp, getTokenExp } from "../jwt";
import {
  AUTH_AUTHENTICATE_REQUEST,
  AUTH_AUTHENTICATE_SUCCESS,
  AUTH_AUTHENTICATE_FAILURE,
  AUTH_LOGIN_REQUEST,
  AUTH_LOGIN_FAILURE,
  AUTH_LOGIN_SUCCESS,
  AUTH_LOGOUT_REQUEST,
  AUTH_LOGOUT_FAILURE,
  AUTH_LOGOUT_SUCCESS,
  AUTH_REFRESH_JWT_REQUEST,
  AUTH_REFRESH_JWT_SUCCESS,
  AUTH_REFRESH_JWT_FAILURE,
  AUTH_JWT_EXPIRED,
} from "./types";
import { initAppData } from "./initActions";
import { notificationDisplay } from "./notificationActions";

const DEFAULT_ERROR = "Authentication service error.";

// ---------------- JWT ----------------

const tokenExpired = () => ({
  type: AUTH_JWT_EXPIRED,
});

const handleAccessToken = (dispatch, token) => {
  const tokenData = jwtDecode(token);
  const nowInSec = Math.floor(Date.now() / 1000);
  const secToExp = tokenData.exp - nowInSec;
  if (secToExp > JWT_REFRESH_WINDOW_SEC) {
    setTokenExp(tokenData.exp);
    setAccessToken(token);

    setTimeout(
      () => dispatch(RefreshJWT()),
      (secToExp - JWT_REFRESH_WINDOW_SEC) * 1000
    );
  } else {
    console.log("invalid token");
  }
};

// ---------------- AUTHENTICATE ----------------
const authenticateRequest = () => ({
  type: AUTH_AUTHENTICATE_REQUEST,
});
const authenticateSuccess = (user) => ({
  type: AUTH_AUTHENTICATE_SUCCESS,
  user,
});

const authenticateFailure = (error) => ({
  type: AUTH_AUTHENTICATE_FAILURE,
  error,
});

export const Authenticate = () => async (dispatch) => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  if (!refreshToken) {
    dispatch(authenticateFailure("No refresh token."));
    return;
  }
  dispatch(authenticateRequest());
  let resp, status, detail;
  try {
    resp = await api.auth.authenticate(refreshToken);
    status = resp.status;
    switch (status) {
      case 200: {
        const { user, token } = await resp.json();
        dispatch(authenticateSuccess(user));
        handleAccessToken(dispatch, token);
        dispatch(initAppData());
        break;
      }
      case 400:
      case 401: {
        localStorage.removeItem(REFRESH_TOKEN);
        const refreshTokenError = await resp.json();
        dispatch(authenticateFailure(refreshTokenError.detail));
        break;
      }
      case 500: {
        const respData = await resp.json();
        dispatch(authenticateFailure(respData.detail));
        break;
      }
      case 422:
        detail = await resp.json().detail;
        dispatch(
          notificationDisplay(
            "error",
            detail.map((d) => `${d.loc[d.loc.length - 1]}: ${d.msg}`)
          )
        );
        dispatch(authenticateFailure(detail));
        break;
      default:
        console.log(AUTH_AUTHENTICATE_FAILURE, status);
        dispatch(authenticateFailure(DEFAULT_ERROR));
        break;
    }
  } catch (error) {
    console.log(AUTH_AUTHENTICATE_FAILURE, error);
    dispatch(authenticateFailure(DEFAULT_ERROR));
  }
};

// ---------------- LOGIN ----------------
const loginRequest = () => ({
  type: AUTH_LOGIN_REQUEST,
});

const loginSuccess = (user, token) => ({
  type: AUTH_LOGIN_SUCCESS,
  user,
  token,
});

const loginFailure = (error) => ({
  type: AUTH_LOGIN_FAILURE,
  error,
});

export const Login = (email, password) => async (dispatch) => {
  dispatch(loginRequest());
  let resp, status, detail;
  try {
    resp = await api.auth.login(email, password);
    status = resp.status;
    switch (status) {
      case 200: {
        const { token, refresh_token, user } = await resp.json();
        localStorage.setItem(REFRESH_TOKEN, refresh_token);
        dispatch(loginSuccess(user, token));
        handleAccessToken(dispatch, token);
        dispatch(initAppData());
        break;
      }
      case 401:
      case 403:
      case 500: {
        const respData = await resp.json();
        dispatch(loginFailure(respData.detail));
        dispatch(notificationDisplay("error", [respData.detail]));
        break;
      }
      case 422:
        detail = await resp.json().detail;
        dispatch(
          notificationDisplay(
            "error",
            detail.map((d) => `${d.loc[d.loc.length - 1]}: ${d.msg}`)
          )
        );
        dispatch(loginFailure(detail));
        break;
      default:
        console.log(AUTH_LOGIN_FAILURE, status);
        dispatch(loginFailure(DEFAULT_ERROR));
        break;
    }
  } catch (error) {
    console.log(AUTH_LOGIN_FAILURE, error);
    dispatch(loginFailure(DEFAULT_ERROR));
  }
};

// ---------------- LOGOUT ----------------
const logoutRequest = () => ({
  type: AUTH_LOGOUT_REQUEST,
});

const logoutSuccess = () => ({
  type: AUTH_LOGOUT_SUCCESS,
});

const logoutFailure = (error) => ({
  type: AUTH_LOGOUT_FAILURE,
  error,
});

export const Logout = (global = false) => async (dispatch) => {
  if (!global) {
    localStorage.removeItem(REFRESH_TOKEN);
    dispatch(logoutSuccess());
    return;
  }
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  if (!refreshToken) {
    dispatch(logoutFailure("No refresh token."));
    return;
  }
  dispatch(logoutRequest());
  let resp, status, detail;
  try {
    resp = await api.auth.logout(refreshToken);
    status = resp.status;
    switch (status) {
      case 200:
        localStorage.removeItem(REFRESH_TOKEN);
        dispatch(logoutSuccess());
        break;
      case 500: {
        const respData = await resp.json();
        dispatch(logoutFailure(respData.detail));
        break;
      }
      case 422:
        detail = await resp.json().detail;
        dispatch(
          notificationDisplay(
            "error",
            detail.map((d) => `${d.loc[d.loc.length - 1]}: ${d.msg}`)
          )
        );
        dispatch(logoutFailure(detail));
        break;
      default:
        console.log(AUTH_LOGOUT_FAILURE, status);
        dispatch(logoutFailure(DEFAULT_ERROR));
        break;
    }
  } catch (error) {
    console.log(AUTH_LOGOUT_FAILURE, error);
    dispatch(logoutFailure(DEFAULT_ERROR));
  }
};

// ---------------- REFRESH JWT ----------------
const refreshJWTRequest = () => ({
  type: AUTH_REFRESH_JWT_REQUEST,
});

const refreshJWTSuccess = () => ({
  type: AUTH_REFRESH_JWT_SUCCESS,
});

const refreshJWTFailure = (error) => ({
  type: AUTH_REFRESH_JWT_FAILURE,
  error,
});

export const RefreshJWT = () => async (dispatch) => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN);
  if (!refreshToken) {
    dispatch(refreshJWTFailure("No refresh token."));
    return;
  }
  dispatch(refreshJWTRequest());
  try {
    const resp = await api.auth.refreshJWT(refreshToken);
    const status = resp.status;
    let respError = null;
    switch (status) {
      case 200: {
        const respData = await resp.json();
        dispatch(refreshJWTSuccess());
        handleAccessToken(dispatch, respData.token);
        break;
      }
      case 400:
      case 401:
        respError = await resp.json();
        localStorage.removeItem(REFRESH_TOKEN);
        dispatch(refreshJWTFailure(respError.detail));
        break;
      default: {
        console.log(AUTH_REFRESH_JWT_FAILURE, status);
        dispatch(refreshJWTFailure(DEFAULT_ERROR));
        const nowInSec = Math.floor(Date.now() / 1000);
        if (nowInSec < getTokenExp()) {
          setTimeout(() => dispatch(RefreshJWT()), JWT_RETRY_TIMEOUT);
        } else {
          dispatch(tokenExpired());
        }
        break;
      }
    }
  } catch (error) {
    console.log(AUTH_REFRESH_JWT_FAILURE, error);
    dispatch(refreshJWTFailure(DEFAULT_ERROR));
    setTimeout(() => dispatch(RefreshJWT()), JWT_RETRY_TIMEOUT);
  }
};
